/** @jsx jsx */
import type { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react';
import { Fragment, forwardRef, useEffect, useMemo, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import type { FileRejection } from 'react-dropzone';
import { Button } from '@balance-web/button';
import { jsx } from '@balance-web/core';
import { buildDataAttributes } from '@balance-web/core';
import type { CSSObject, WithDataAttributeProp } from '@balance-web/core';
import { UploadIcon } from '@balance-web/icon/icons/UploadIcon';
import { Stack } from '@balance-web/stack';
import { Text } from '@balance-web/text';
import { useTheme } from '@balance-web/theme';
import { useForkedRef } from '@balance-web/utils';
import { Portal } from '@balance-web/portal';

type FileInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'title'>;

export type FileRejectionProps = FileRejection;

export type FileUploadProps = WithDataAttributeProp<
  {
    title?: ReactNode;
    description?: ReactNode;
    dropZoneHeight?: string | number;
    dropZonePaddingTop?: string | number;
    dropZonePaddingBottom?: string | number;
    inlineDropZone?: boolean;
    windowDropZone?: boolean;
    children?: ReactNode;
    onFilesSelected: (files: File[]) => void;
    onFilesRejected?: (fileRejections: FileRejection[]) => void;
  } & FileInputProps
>;

let windowDropZoneCounter = 0;

const isFileDrag = (event: DragEvent) =>
  Array.from(event.dataTransfer?.types ?? []).includes('Files');

export const FileUpload = forwardRef<HTMLInputElement, FileUploadProps>(
  (
    {
      title,
      description,
      inlineDropZone,
      windowDropZone,
      dropZoneHeight,
      dropZonePaddingTop,
      dropZonePaddingBottom,
      disabled,
      onFilesSelected,
      onFilesRejected,
      children,
      multiple = true,
      data,
      ...inputProps
    },
    forwardedRef
  ) => {
    const { semantic, radii, elevation } = useTheme();
    const internalRef = useRef<HTMLInputElement>(null);
    const forkedRef = useForkedRef(internalRef, forwardedRef);

    const rootElementRef = useRef<HTMLDivElement>(null);

    const windowDropZoneOverlayElementRef = useRef<HTMLDivElement | null>(null);

    const formattedAcceptAttribute = (inputProps.accept || '')
      .split(',')
      .reduce((prev, curr) => {
        return { ...prev, ...{ [curr]: [] } };
      }, {});

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      // @ts-ignore Merging HTMLInput props with react-dropzone is causing type collision
      onDrop: (acceptedFiles) => {
        onFilesSelected(acceptedFiles);
        rootElementRef.current?.scrollIntoView({
          behavior: 'smooth',
        });
      },
      onDropRejected: (fileRejections) => {
        rootElementRef.current?.scrollIntoView({
          behavior: 'smooth',
        });
        if (typeof onFilesRejected !== 'function') {
          return;
        }
        onFilesRejected(fileRejections);
      },
      ...inputProps,
      disabled,
      multiple,
      accept: formattedAcceptAttribute,
    });

    const dropZoneStyles: CSSObject = useMemo(() => {
      return {
        width: '100%',
        minHeight: dropZoneHeight || '92px',
        background: semantic.colour.surface.neutral.background1,
        border: `1px solid ${
          isDragActive && !disabled
            ? semantic.colour.primary.background
            : semantic.colour.surface.neutral.border
        }`,
        borderRadius: radii.medium,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        ':hover': {
          background: semantic.colour.surface.neutral.background2,
        },
        ':active': {
          borderStyle: disabled ? undefined : 'solid',
        },
        paddingTop: dropZonePaddingTop ?? '16px',
        paddingBottom: dropZonePaddingBottom ?? '16px',
      };
    }, [
      disabled,
      isDragActive,
      dropZoneHeight,
      dropZonePaddingTop,
      dropZonePaddingBottom,
      semantic.colour.surface.neutral.background1,
      semantic.colour.surface.neutral.background2,
      semantic.colour.surface.neutral.border,
      semantic.colour.primary.background,
      radii.medium,
    ]);

    const dropZoneProps = inlineDropZone
      ? {
          ...getRootProps(),
          css: dropZoneStyles,
        }
      : undefined;

    const internalInputProps = inlineDropZone
      ? { ...getInputProps() }
      : {
          ...inputProps,
          multiple,
          onChange: (event: ChangeEvent<HTMLInputElement>) => {
            onFilesSelected(Array.from(event.target.files || []));
          },
        };

    const handleClick = () => {
      internalRef.current?.click();
    };

    useEffect(() => {
      if (!windowDropZone) {
        return;
      }

      const controller = new AbortController();

      const showOverlay = () => {
        if (windowDropZoneOverlayElementRef.current) {
          windowDropZoneOverlayElementRef.current.style.display = 'flex';
        }
      };

      const hideOverlay = () => {
        if (windowDropZoneOverlayElementRef.current) {
          windowDropZoneOverlayElementRef.current.style.display = 'none';
        }
      };

      document.body.addEventListener(
        'dragenter',
        (e) => {
          if (!isFileDrag(e)) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          showOverlay();
        },
        {
          signal: controller.signal,
        }
      );

      document.body.addEventListener(
        'dragover',
        (e) => {
          if (!isFileDrag(e)) {
            return;
          }
          e.preventDefault();
        },
        {
          signal: controller.signal,
        }
      );

      document.body.addEventListener(
        'dragleave',
        (e) => {
          if (!isFileDrag(e)) {
            return;
          }
          e.preventDefault();
          if (e.target === windowDropZoneOverlayElementRef.current) {
            hideOverlay();
          }
        },
        {
          signal: controller.signal,
        }
      );

      document.body.addEventListener(
        'drop',
        (e) => {
          if (!isFileDrag(e)) {
            return;
          }
          e.preventDefault();

          if (
            e.target instanceof Node &&
            e.target !== windowDropZoneOverlayElementRef.current &&
            !windowDropZoneOverlayElementRef.current?.contains(e.target)
          ) {
            return;
          }

          hideOverlay();
          if (internalRef.current) {
            const clonedEvent = new DragEvent('drop', e);
            internalRef.current.dispatchEvent(clonedEvent);
          }
        },
        {
          signal: controller.signal,
        }
      );

      return () => {
        controller.abort();
      };
    }, [windowDropZone]);

    const dataAttributes = buildDataAttributes(data);

    useEffect(
      function trackWindowDropZoneCounter() {
        if (windowDropZone) {
          windowDropZoneCounter += 1;
        }

        return () => {
          if (windowDropZone) {
            windowDropZoneCounter -= 1;
          }
        };
      },
      [windowDropZone]
    );

    if (windowDropZoneCounter > 1) {
      throw Error(
        'There can only be 1 instance of FileUpload component with windowDropZone=true'
      );
    }

    if (dropZoneProps) {
      dropZoneProps.onClick = handleClick;
    }

    return (
      <Fragment>
        <Stack ref={rootElementRef} gap="large">
          {!inlineDropZone ? (
            typeof title === 'string' && title?.length ? (
              <Text weight="semibold">{title}</Text>
            ) : (
              title
            )
          ) : null}
          <div {...dropZoneProps}>
            {inlineDropZone ? (
              <Stack align="center" flex="1" gap="medium">
                <UploadIcon color={disabled ? 'textDisabled' : 'textLink'} />
                <Text
                  color={disabled ? 'textDisabled' : 'textPrimary'}
                  size="medium"
                  weight="medium"
                >
                  {typeof title === 'string' && title?.length ? (
                    <Text weight="semibold">{title}</Text>
                  ) : (
                    title
                  )}
                </Text>
                {typeof description === 'string' && description.length ? (
                  <Text color="textBody" size="xsmall">
                    {description}
                  </Text>
                ) : (
                  description
                )}
              </Stack>
            ) : (
              <Button
                iconBefore={UploadIcon}
                label="Upload file"
                size="small"
                variant="outline"
                onClick={handleClick}
              />
            )}
            <input
              css={{ display: 'none' }}
              type="file"
              {...dataAttributes}
              {...internalInputProps}
              ref={forkedRef}
            />
          </div>

          {children}
        </Stack>
        {windowDropZone ? (
          <Portal>
            <div
              ref={windowDropZoneOverlayElementRef}
              css={[
                {
                  backgroundColor: 'rgba(90, 63, 255, 0.16)',
                  backdropFilter: 'blur(2px)',
                  bottom: 0,
                  left: 0,
                  position: 'fixed',
                  right: 0,
                  top: 0,
                  zIndex: elevation['modal'],

                  alignItems: 'center',
                  display: 'none',
                  justifyContent: 'center',
                },
              ]}
            >
              <Stack
                align="center"
                background="base"
                border="standard"
                borderRadius="xlarge"
                css={{ justifyContent: 'center' }}
                gap="medium"
                minHeight={140}
                minWidth={360}
                padding="xxlarge"
              >
                <UploadIcon color="textLink" />

                <Text
                  css={{
                    color: semantic.colour.text.textSecondary,
                  }}
                >
                  Drop anywhere to upload files
                </Text>

                {typeof description === 'string' && description.length ? (
                  <Text color="textBody" size="xsmall">
                    {description}
                  </Text>
                ) : (
                  description
                )}
              </Stack>
            </div>
          </Portal>
        ) : null}
      </Fragment>
    );
  }
);
